import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL}`;

const getAuthHeaders = () => {
  // For user portal endpoints, use user portal token
  // For trainer/admin endpoints, use access token
  const currentPath = window.location.pathname;
  const isUserPortal = currentPath === '/user-portal';
  
  const token = isUserPortal 
    ? (localStorage.getItem('userPortalAccessToken') || localStorage.getItem('accessToken'))
    : localStorage.getItem('accessToken');
  
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

const getAuthHeadersMultipart = () => {
  const token = localStorage.getItem('accessToken');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  };
};

export const uploadVideo = async (file, onProgress) => {
  try {
    const formData = new FormData();
    formData.append('video', file);

    const response = await axios.post(
      `${API_URL}video-lessons/upload`,
      formData,
      {
        ...getAuthHeadersMultipart(),
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (onProgress) {
            onProgress(percentCompleted);
          }
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createVideoLesson = async (videoData, file, thumbnailFile, onProgress) => {
  try {
    const formData = new FormData();
    formData.append('title', videoData.title);
    if (videoData.description) {
      formData.append('description', videoData.description);
    }
    if (file) {
      formData.append('video', file);
    }
    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile);
    }

    const response = await axios.post(
      `${API_URL}video-lessons`,
      formData,
      {
        ...getAuthHeadersMultipart(),
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (onProgress) {
            onProgress(percentCompleted);
          }
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getMyVideoLessons = async (page = 1, limit = 9, search = '') => {
  try {
    let url = `${API_URL}video-lessons/trainer/my-lessons?page=${page}&limit=${limit}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    const response = await axios.get(
      url,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getAllPublicVideoLessons = async (page = 1, limit = 10) => {
  try {
    const response = await axios.get(
      `${API_URL}video-lessons/public?page=${page}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getVideoLessonById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}video-lessons/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateVideoLesson = async (id, updateData) => {
  try {
    const response = await axios.put(
      `${API_URL}video-lessons/${id}`,
      updateData,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteVideoLesson = async (id) => {
  try {
    const response = await axios.delete(
      `${API_URL}video-lessons/${id}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const incrementViewCount = async (id) => {
  try {
    const response = await axios.post(`${API_URL}video-lessons/${id}/view`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const searchVideoLessons = async (searchTerm, page = 1, limit = 10) => {
  try {
    const response = await axios.get(
      `${API_URL}video-lessons/search?q=${searchTerm}&page=${page}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getSignedVideoUrl = async (videoUrl) => {
  try {
    const response = await axios.get(
      `${API_URL}video-lessons/signed-url?videoUrl=${encodeURIComponent(videoUrl)}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// New endpoints for access control
export const getMyTrainersVideos = async (page = 1, limit = 10) => {
  try {
    const response = await axios.get(`${API_URL}video-lessons/user/my-trainers?page=${page}&limit=${limit}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getOtherTrainersVideos = async (page = 1, limit = 10) => {
  try {
    const response = await axios.get(`${API_URL}video-lessons/user/other-trainers?page=${page}&limit=${limit}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getAllVideosForUser = async (page = 1, limit = 10) => {
  try {
    const response = await axios.get(`${API_URL}video-lessons/user/all-videos?page=${page}&limit=${limit}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const checkVideoAccess = async (videoId) => {
  try {
    const response = await axios.get(`${API_URL}video-lessons/user/check-access/${videoId}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

